import React, { useContext, useEffect, useRef, useState } from "react";
import "./timeLineBar.css";
import { ConstantProperty, JulianDate } from "cesium";
import ViewerContext from "../../context/ViewerContext";
import { getDataSource } from "../../helper/cesiumHelper";

const allowedMultipliers = [1, 2, 3, 5, 10, 15, 30, 60, 120, 240];
const FORWARD_BACWARD_TIME = 1;
const TimeLineBarComponent = () => {
  const { igcParsedData, coloredFile, setTrailDuration, autoSwitchTail, setAutoSwitchTail } =
    useContext(ViewerContext);

  const [isPlaying, setIsPlaying] = useState();
  const [multiplier, setMultiplier] = useState();
  const [currentTime, setCurrentTime] = useState();
  const [showToolbar, setShowToolbar] = useState(true);
  const [switchTrailTime, setSwitchTrailTime] = useState(false);

  const multiplierRef = useRef();

  useEffect(() => {
    multiplierRef.current = multiplier;
  }, [multiplier]);

  // Auto-enable Switch Tail Time when more than 5 tracks are loaded
  useEffect(() => {
    if (autoSwitchTail && !switchTrailTime) {
      setSwitchTrailTime(true); // Just update the UI state
      setAutoSwitchTail(false); // Reset flag after using
    }
  }, [autoSwitchTail]);

  useEffect(() => {
    const updateCurrentTime = () => {
      const time = getLocalTime(viewer.clock.currentTime);
      setCurrentTime(time);

      // Update the displayed multiplier if it changes externally
      const currentMultiplier = viewer.clock.multiplier;
      if (multiplier !== currentMultiplier) {
        setMultiplier(currentMultiplier);
      }

      const currentPlayMode = viewer.clock.shouldAnimate;
      if (isPlaying != currentMultiplier) {
        setIsPlaying(currentPlayMode);
      }
    };

    const onTick = viewer.clock.onTick.addEventListener(updateCurrentTime);

    document.addEventListener("keydown", function (event) {
      if (event.code === "Space") {
        event.preventDefault();
        // Toggle play/pause
        viewer.clock.shouldAnimate = !viewer.clock.shouldAnimate;
      }

      if (event.code === "ArrowRight") {
        handleForward();
      }

      if (event.code === "ArrowLeft") {
        handleBackward();
      }

      if (event.code === "ArrowUp") {
        adjustMultiplier("increase");
      }

      if (event.code === "ArrowDown") {
        adjustMultiplier("decrease");
      }

    });

    // Cleanup when component unmounts
    return () => {
      onTick(); // Remove event listener
    };
  }, []);

  const handlePlayPause = (e) => {
    e.currentTarget.blur(); // Remove focus

    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      viewer.clock.shouldAnimate = true;
    } else {
      viewer.clock.shouldAnimate = false;
    }
  };

  const handleShowHideToolbar = (e) => {
    e.currentTarget.blur();

    setShowToolbar(!showToolbar);
    var toolbar = document.querySelector(".toolbar-container");
    if (toolbar.style.display === "none") {
      toolbar.style.display = "block";
    } else {
      toolbar.style.display = "none";
    }
  };

  const handleSwitchTrailTime = () => {
    const newTrailTime = !switchTrailTime ? 20 * 60 : 80000;
    igcParsedData.forEach((item) => {
      const dataSource = getDataSourceById(item.id);
      if (dataSource) {
        dataSource.entities.values.forEach((entity) => {
          if (entity && entity.path) {
            entity.path.trailTime = new ConstantProperty(newTrailTime);
          }
        });
      }
    });
    setTrailDuration(newTrailTime);
    setSwitchTrailTime(!switchTrailTime);
  };

  const getDataSourceById = (fileId) => {
    return getDataSource(fileId, coloredFile, igcParsedData);
  };

  const handleDecreaseMultiplier = (e) => {
    e.currentTarget.blur();
    adjustMultiplier("decrease");
  };

  const handleIncreaseMultiplier = (e) => {
    e.currentTarget.blur();
    adjustMultiplier("increase");
  };

  const handleBackward = (e) => {
    e?.currentTarget.blur();
    const currentTime = viewer.clock.currentTime;
    const newTime = JulianDate.addMinutes(
      currentTime,
      -1 * FORWARD_BACWARD_TIME,
      new JulianDate()
    );
    viewer.clock.currentTime = newTime;
  };

  const handleForward = (e) => {
    e?.currentTarget.blur();
    const currentTime = viewer.clock.currentTime;
    const newTime = JulianDate.addMinutes(
      currentTime,
      FORWARD_BACWARD_TIME,
      new JulianDate()
    );
    viewer.clock.currentTime = newTime;
  };

  const handleStepBackward = (e) => {
    e?.currentTarget.blur();

    // Set the current time to the end time
    var startTime = viewer.clock.startTime;
    viewer.clock.currentTime = startTime;

    // Pause the clock
    viewer.clock.shouldAnimate = false;
  };

  const handleStepForward = (e) => {
    e?.currentTarget.blur();

    // Set the current time to the end time
    var endTime = viewer.clock.stopTime;
    viewer.clock.currentTime = endTime;

    // Pause the clock
    viewer.clock.shouldAnimate = false;
  };

  const adjustMultiplier = (operation) => {
    const multiplierRefValue = multiplierRef.current;
    let newMultiplier = multiplierRefValue;
    if (operation === "increase") {
      for (let i = 0; i < allowedMultipliers.length; i++) {
        if (allowedMultipliers[i] > multiplierRefValue) {
          newMultiplier = allowedMultipliers[i];
          break;
        }
      }
    } else if (operation === "decrease") {
      for (let i = allowedMultipliers.length - 1; i >= 0; i--) {
        if (allowedMultipliers[i] < multiplierRefValue) {
          newMultiplier = allowedMultipliers[i];
          break;
        }
      }
    }
    setMultiplier(newMultiplier);
    viewer.clock.multiplier = newMultiplier;
  };

  function getLocalTime(time) {
    const localDate = JulianDate.toDate(time);
    const hour = localDate.getHours();
    const minute = localDate.getMinutes();
    const second = localDate.getSeconds();

    return `${twoDigits(hour)}:${twoDigits(minute)}:${twoDigits(second)}`;
  }

  function twoDigits(num) {
    return num.toString().padStart(2, "0");
  }

  return (
    <div className="timeline-bar-container">
      <div className="timeline-bar-controls">
        <button onClick={handleStepBackward}>
          <img
            style={{ transform: "rotate(180deg)" }}
            src="./icons/step-forward.svg"
            alt="Step Backward"
          />
        </button>
        {/* <button onClick={handleBackward}>
                    <img style={{ transform: 'rotate(180deg)' }} src="./icons/forward.svg" alt="Backward" />
                </button> */}
        <button onClick={handlePlayPause}>
          <img
            src={isPlaying ? "./icons/pause.svg" : "./icons/play.svg"}
            alt="Play/Pause"
          />
        </button>
        {/* <button onClick={handleForward}>
                    <img src="./icons/forward.svg" alt="Forward" />
                </button> */}
        <button onClick={handleStepForward}>
          <img src="./icons/step-forward.svg" alt="Step Forward" />
        </button>
      </div>

      <div className="current-time timeline-bar-controls">
        <button onClick={handleShowHideToolbar}>
          <img
            src={showToolbar ? "./icons/show.svg" : "./icons/hide.svg"}
            alt="Show/Hide Toolbar"
          />
        </button>
        <span>{currentTime}</span>
        <button onClick={handleSwitchTrailTime}>
          <img
            src="./icons/tail.svg"
            alt="Switch Tail Time"
            style={{
              filter: switchTrailTime ? "invert(100%)" : "invert(0%)",
            }}
          />
        </button>
      </div>

      <div className="timeline-bar-controls">
        <button onClick={handleDecreaseMultiplier}>
          <img src="./icons/minus.svg" alt="Decrease Speed" />
        </button>

        <span className="multiplier">{multiplier}</span>
        <span>x</span>
        <button onClick={handleIncreaseMultiplier}>
          <img src="./icons/plus.svg" alt="Increase Speed" />
        </button>
      </div>
    </div>
  );
};

export default TimeLineBarComponent;
